import type { ReactNode } from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export interface PageSectionProps {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Removes the inner padding — use when the child is a DataTable or chart. */
  disablePadding?: boolean;
}

/** A titled card. The default container for anything on a page. */
export function PageSection({
  title,
  description,
  actions,
  children,
  disablePadding = false,
}: PageSectionProps) {
  const theme = useTheme();
  const hasHeader = Boolean(title || actions);

  return (
    <Card>
      {hasHeader && (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: disablePadding ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && <Typography variant="h4">{title}</Typography>}
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {description}
              </Typography>
            )}
          </Box>
          {actions && (
            <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
              {actions}
            </Stack>
          )}
        </Stack>
      )}
      <Box sx={disablePadding ? undefined : { p: 2.5, pt: hasHeader ? 0 : 2.5 }}>{children}</Box>
    </Card>
  );
}

export interface StatTileProps {
  label: string;
  value: string | number;
  hint?: string;
  icon?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Makes the tile a filter control: it drills into whatever the number counts.
   *
   * Optional, and a tile without it stays exactly as plain as before — most
   * tiles in the portal are read-outs with nothing sensible to drill into, and
   * a card that looks clickable but is not is worse than one that does not.
   */
  onClick?: () => void;
  /** Whether this tile's filter is the one currently applied. */
  selected?: boolean;
}

/** Single metric tile. Kept deliberately plain — no sparklines, no gradients. */
export function StatTile({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  onClick,
  selected = false,
}: StatTileProps) {
  const theme = useTheme();
  const toneColor = tone === 'default' ? theme.palette.text.primary : theme.palette[tone].main;

  return (
    <Card
      sx={{
        p: 2.5,
        height: '100%',
        ...(onClick && {
          cursor: 'pointer',
          textAlign: 'start',
          width: '100%',
          display: 'block',
          transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
          // The selected tile carries its own tone so it is obvious WHICH filter
          // is on when four tiles sit side by side.
          borderColor: selected ? toneColor : undefined,
          boxShadow: selected ? `inset 0 0 0 1px ${toneColor}` : undefined,
          '&:hover': { bgcolor: 'action.hover' },
          // Keyboard users get the same affordance; a div with onClick gives
          // them nothing, which is why this renders as a real button below.
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }),
      }}
      {...(onClick
        ? {
            component: 'button' as const,
            type: 'button' as const,
            onClick,
            'aria-pressed': selected,
          }
        : {})}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {label}
          </Typography>
          <Typography variant="h2" sx={{ mt: 0.5, color: toneColor }}>
            {value}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {hint}
            </Typography>
          )}
        </Box>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: `${theme.custom.radius.md}px`,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'action.hover',
              color: toneColor,
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
    </Card>
  );
}
